import ApiService from './api';
import { safeArray } from '../utils/money';

export interface Channel {
  id: string;
  name: string;
  provider_type: string;
  enabled: boolean;
  config: Record<string, any>;
  supported_currencies: string[];
  supported_countries: string[];
  payment_methods: string[];
  icon?: string;
  description?: string;
}

// 渠道展示名映射
const CHANNEL_NAME_MAP: Record<string, string> = {
  connect: 'Stripe Connect 渠道',
  default: '平台默认渠道',
  stripe: 'Stripe',
  epay: 'ePay 渠道',
};

const CHANNEL_DESC_MAP: Record<string, string> = {
  connect: '通过 Stripe Connect 受理信用卡等支付方式',
  default: '平台聚合渠道，支持银行转账、加密货币等',
};

/**
 * 把后端渠道结构 { channel_id, enabled, channel_params, payment_methods }
 * 归一化为前端 Channel 结构，避免字段为空导致页面显示空白。
 */
function normalizeChannel(raw: any): Channel {
  const id = raw.channel_id || raw.id || '';
  return {
    id,
    name: raw.name || CHANNEL_NAME_MAP[id] || id || '未命名渠道',
    provider_type: raw.provider_type || id || '-',
    enabled: !!raw.enabled,
    config: raw.channel_params || raw.config || {},
    supported_currencies: safeArray<string>(raw.supported_currencies),
    supported_countries: safeArray<string>(raw.supported_countries),
    payment_methods: safeArray<string>(raw.payment_methods),
    icon: raw.icon,
    description: raw.description || CHANNEL_DESC_MAP[id],
  };
}

export class ChannelService {
  /**
   * 获取商户渠道列表
   */
  static async getMerchantChannels(): Promise<Channel[]> {
    const response = await ApiService.get<any[]>('/merchant/channels');
    return safeArray<any>(response.data).map(normalizeChannel);
  }

  /**
   * 获取渠道详情
   */
  static async getChannel(id: string): Promise<Channel> {
    const response = await ApiService.get<Channel>(`/merchant/channels/${id}`);
    return response.data;
  }

  /**
   * 更新渠道配置
   */
  static async updateChannelConfig(
    id: string,
    config: Record<string, any>
  ): Promise<Channel> {
    const response = await ApiService.put<Channel>(`/merchant/channels/${id}`, {
      config,
    });
    return response.data;
  }

  /**
   * 测试渠道连接
   */
  static async testChannel(id: string): Promise<{
    success: boolean;
    message: string;
    details?: any;
  }> {
    const response = await ApiService.post(`/merchant/channels/${id}/test`);
    return response.data;
  }

  /**
   * 获取渠道要求
   */
  static async getChannelRequirements(id: string): Promise<{
    required_fields: string[];
    optional_fields: string[];
    documentation_url?: string;
  }> {
    const response = await ApiService.get(`/merchant/channels/${id}/requirements`);
    return response.data;
  }

  /**
   * 启用渠道
   */
  static async enableChannel(id: string): Promise<Channel> {
    const response = await ApiService.put<Channel>(`/merchant/channels/${id}`, {
      enabled: true,
    });
    return response.data;
  }

  /**
   * 禁用渠道
   */
  static async disableChannel(id: string): Promise<Channel> {
    const response = await ApiService.put<Channel>(`/merchant/channels/${id}`, {
      enabled: false,
    });
    return response.data;
  }
}

export default ChannelService;
